import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import { RouterProvider, createBrowserRouter, useLocation, useRouteError } from "react-router-dom";
import TrackPageViews from "./components/Analytics/TrackPageViews";
import "./index.css";
import HomePage from "./pages/Home/HomePage";
import ErrorPage from "./components/ErrorPage/ErrorPage";
import ServicesPage from "./pages/Services/ServicesPage";
import TechPage from "./pages/Technologies/TechPage";
import CaseStudies from "./pages/CaseStudies/CaseStudies";
import HireUs from "./pages/HireUs/HireUs";
import Blog from "./pages/Blog/Blogs";
import JoinUs from "./pages/JoinUs/JoinUs";
import BlogDetails from "./pages/Blog/BlogDetails";
import AuthorProfile from "./pages/Blog/AuthorProfile";
import Activities from "./pages/Activities/Activities";
import PrivacyPage from "./pages/Privacy Policy/PrivacyPage";
import TermsPage from "./pages/Terms/TermsPage";
import ConfirmationPage from "./pages/Confirmation/ConfirmationPage";
import Visibility from "./pages/Visibility/visibility";
import Support from "./pages/Support/Support";
import BelaChatWidget from "./components/Bela/BelaChatWidget";
import ScrollToTop from "./components/ScrollToTop";

const AppRoute = ({ children }) => {
  const location = useLocation();
  
  if (location.pathname.startsWith('/belsoft-blog')) {
    if (process.env.NODE_ENV === 'production') {
      window.location.replace(`https://belsoft-blog.sanity.studio${location.pathname.replace('/belsoft-blog', '')}`);
      return null;
    }
    
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500 mx-auto mb-4"></div>
          <p>Loading Sanity Studio...</p>
          <p className="text-sm text-gray-500 mt-2">If you're not redirected automatically, 
            <a 
              href={`http://localhost:3333${location.pathname.replace('/belsoft-blog', '')}`} 
              className="text-blue-600 hover:underline ml-1"
              target="_blank"
              rel="noopener noreferrer"
            >
              click here
            </a>
          </p>
        </div>
      </div>
    );
  }
  
  return (
    <BelaChatWidget>
      <ScrollToTop />
      <TrackPageViews />
      {children}
    </BelaChatWidget>
  );
};

const ErrorBoundaryWrapper = ({ children }) => {
  let error;
  try {
    error = useRouteError?.();
  } catch (e) {
    console.error('Error in useRouteError:', e);
  }
  
  if (error) {
    return <ErrorPage error={error} />;
  }
  
  return children;
};

const router = createBrowserRouter([
  { 
    path: "/", 
    element: (
      <ErrorBoundaryWrapper>
        <AppRoute>
          <HomePage />
        </AppRoute>
      </ErrorBoundaryWrapper>
    ),
    errorElement: <ErrorPage />
  },
  { 
    path: "/our-services", 
    element: (
      <ErrorBoundaryWrapper>
        <AppRoute>
          <ServicesPage />
        </AppRoute>
      </ErrorBoundaryWrapper>
    ),
    errorElement: <ErrorPage />
  },
  { 
    path: "/technologies", 
    element: (
      <ErrorBoundaryWrapper>
        <AppRoute>
          <TechPage />
        </AppRoute>
      </ErrorBoundaryWrapper>
    ),
    errorElement: <ErrorPage />
  },
  { 
    path: "/case-studies", 
    element: (
      <ErrorBoundaryWrapper>
        <AppRoute>
          <CaseStudies />
        </AppRoute>
      </ErrorBoundaryWrapper>
    ),
    errorElement: <ErrorPage />
  },
  { 
    path: "/hire-us", 
    element: (
      <ErrorBoundaryWrapper>
        <AppRoute>
          <HireUs />
        </AppRoute>
      </ErrorBoundaryWrapper>
    ),
    errorElement: <ErrorPage />
  },
  { 
    path: "/blog", 
    element: (
      <ErrorBoundaryWrapper>
        <AppRoute>
          <Blog />
        </AppRoute>
      </ErrorBoundaryWrapper>
    ),
    errorElement: <ErrorPage />
  },
  { 
    path: "/join-us", 
    element: (
      <ErrorBoundaryWrapper>
        <AppRoute>
          <JoinUs />
        </AppRoute>
      </ErrorBoundaryWrapper>
    ),
    errorElement: <ErrorPage />
  },
  { 
    path: "/blog/:slug", 
    element: (
      <ErrorBoundaryWrapper>
        <AppRoute>
          <BlogDetails />
        </AppRoute>
      </ErrorBoundaryWrapper>
    ),
    errorElement: <ErrorPage />
  },
  { 
    path: "/author/:slug", 
    element: (
      <ErrorBoundaryWrapper>
        <AppRoute>
          <AuthorProfile />
        </AppRoute>
      </ErrorBoundaryWrapper>
    ),
    errorElement: <ErrorPage />
  },
  { 
    path: "/activities", 
    element: (
      <ErrorBoundaryWrapper>
        <AppRoute>
          <Activities />
        </AppRoute>
      </ErrorBoundaryWrapper>
    ),
    errorElement: <ErrorPage />
  },
  { 
    path: "/privacy-policy", 
    element: (
      <ErrorBoundaryWrapper>
        <AppRoute>
          <PrivacyPage />
        </AppRoute>
      </ErrorBoundaryWrapper>
    ),
    errorElement: <ErrorPage />
  },
  { 
    path: "/terms-of-service", 
    element: (
      <ErrorBoundaryWrapper>
        <AppRoute>
          <TermsPage />
        </AppRoute>
      </ErrorBoundaryWrapper>
    ),
    errorElement: <ErrorPage />
  },
  {
    path: "/support",
    element: (
      <ErrorBoundaryWrapper>
        <AppRoute>
          <Support />
        </AppRoute>
      </ErrorBoundaryWrapper>
    ),
    errorElement: <ErrorPage />
  },
  { 
    path: "/visibility", 
    element: (
      <ErrorBoundaryWrapper>
        <AppRoute>
          <Visibility />
        </AppRoute>
      </ErrorBoundaryWrapper>
    ),
    errorElement: <ErrorPage />
  },
  { 
    path: "/confirmation", 
    element: (
      <ErrorBoundaryWrapper>
        <AppRoute>
          <ConfirmationPage />
        </AppRoute>
      </ErrorBoundaryWrapper>
    ),
    errorElement: <ErrorPage />
  },
  {
    path: "*",
    element: (
      <AppRoute>
        <ErrorPage error={{ status: 404, statusText: 'Not Found' }} />
      </AppRoute>
    )
  }
]);

const App = () => {
  return (
    <StrictMode>
      <ErrorBoundaryWrapper>
        <RouterProvider router={router} />
      </ErrorBoundaryWrapper>
    </StrictMode>
  );
};

createRoot(document.getElementById("root")).render(<App />);
